/**
 * Attendance Service
 * Handles daily attendance check-in/check-out in Firestore
 */
import { getFirebaseDb } from '../firebase/firebase.js'
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDocs,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  arrayUnion,
} from 'firebase/firestore'

// Subcollection path: Agent_portal/Agent_portal/Attendance
const COLLECTION_PATH = ['Agent_portal', 'Agent_portal', 'Attendance']

/**
 * Get the Firestore collection reference for attendance
 * @returns {CollectionReference}
 */
function getAttendanceCollection() {
  const db = getFirebaseDb()
  return collection(db, ...COLLECTION_PATH)
}

/**
 * Parse year and month numbers from a YYYY-MM-DD date string
 * @param {string} dateStr - 'YYYY-MM-DD'
 * @returns {{ year: number, month: number }}
 */
function parseDateParts(dateStr) {
  const [year, month] = dateStr.split('-').map(Number)
  return { year, month }
}

/**
 * Get today's date string in YYYY-MM-DD format
 * @returns {string}
 */
function getTodayString() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Check in for today
 * @param {Object} data - { agentUid, agentName, agentNumber }
 * @returns {Promise<{ success: boolean, docId: string }>}
 */
export async function checkIn({ agentUid, agentName, agentNumber }) {
  const date = getTodayString()
  const { year, month } = parseDateParts(date)

  const attendanceDoc = {
    agentUid,
    agentName,
    agentNumber: String(agentNumber),
    date,
    year,
    month,
    checkIn: serverTimestamp(),
    checkOut: null,
    createdAt: serverTimestamp(),
  }

  const docRef = await addDoc(getAttendanceCollection(), attendanceDoc)
  return { success: true, docId: docRef.id }
}

/**
 * Check out (update existing attendance doc)
 * @param {string} docId - Attendance document ID
 * @returns {Promise<{ success: boolean }>}
 */
export async function checkOut(docId) {
  const db = getFirebaseDb()
  const docRef = doc(db, ...COLLECTION_PATH, docId)

  await updateDoc(docRef, {
    checkOut: serverTimestamp(),
  })

  return { success: true }
}

/**
 * Get today's attendance record for an agent
 * @param {string} agentUid - Agent's Firebase UID
 * @returns {Promise<Object|null>} Attendance doc or null
 */
export async function getTodayAttendance(agentUid) {
  const date = getTodayString()

  const q = query(
    getAttendanceCollection(),
    where('agentUid', '==', agentUid),
    where('date', '==', date)
  )

  const snapshot = await getDocs(q)
  if (snapshot.empty) return null

  // Prefer the active (no checkOut) record; otherwise pick the latest by createdAt
  const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))
  const active = docs.find((d) => d.checkIn && !d.checkOut)
  if (active) return active

  docs.sort((a, b) => {
    const aTime = a.createdAt?.seconds || 0
    const bTime = b.createdAt?.seconds || 0
    return bTime - aTime
  })
  return docs[0]
}

/**
 * Subscribe to attendance records for a specific month (realtime)
 * @param {string} agentUid - Agent's Firebase UID
 * @param {number} year - Year (e.g., 2026)
 * @param {number} month - Month (1-12)
 * @param {Function} callback - (records, error) => void
 * @returns {Function} Unsubscribe function
 */
export function subscribeMonthAttendance(agentUid, year, month, callback) {
  const startDate = `${year}-${String(month).padStart(2, '0')}-01`
  const endDate = `${year}-${String(month).padStart(2, '0')}-31`

  const q = query(
    getAttendanceCollection(),
    where('agentUid', '==', agentUid),
    where('date', '>=', startDate),
    where('date', '<=', endDate),
    orderBy('date', 'desc')
  )

  return onSnapshot(
    q,
    (snapshot) => {
      const records = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }))
      callback(records, null)
    },
    (error) => {
      callback(null, error)
    }
  )
}

// ============================================
// Admin Functions
// ============================================

/**
 * Subscribe to ALL agents' attendance for a date range (admin view)
 * @param {string} startDate - 'YYYY-MM-DD'
 * @param {string|null} endDate - 'YYYY-MM-DD' or null for open-ended
 * @param {Function} callback - (records, error) => void
 * @returns {Function} Unsubscribe function
 */
export function subscribeAllAttendance(startDate, endDate, callback) {
  const constraints = [where('date', '>=', startDate)]
  if (endDate) {
    constraints.push(where('date', '<=', endDate))
  }
  constraints.push(orderBy('date', 'desc'))

  const q = query(getAttendanceCollection(), ...constraints)

  return onSnapshot(
    q,
    (snapshot) => {
      const records = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }))
      callback(records, null)
    },
    (error) => {
      callback(null, error)
    }
  )
}

/**
 * Helper: convert 'HH:MM' time string + date string to Firestore Timestamp
 * @param {string} dateStr - 'YYYY-MM-DD'
 * @param {string} timeStr - 'HH:MM'
 * @returns {Timestamp}
 */
function toTimestamp(dateStr, timeStr) {
  const [hours, minutes] = timeStr.split(':').map(Number)
  const [year, month, day] = dateStr.split('-').map(Number)
  const date = new Date(year, month - 1, day, hours, minutes, 0)
  return Timestamp.fromDate(date)
}

/**
 * Admin: add attendance record with explicit times
 * @param {Object} data
 * @returns {Promise<{ success: boolean, docId: string }>}
 */
export async function adminAddAttendance({
  agentUid,
  agentName,
  agentNumber,
  date,
  checkInTime,
  checkOutTime,
  adminUser,
  vacation = false,
  vacation_type = null,
  vacation_start = null,
  vacation_end = null,
}) {
  const effectiveDate = vacation ? vacation_start : date
  const { year, month } = parseDateParts(effectiveDate)

  const attendanceDoc = {
    agentUid,
    agentName,
    agentNumber: String(agentNumber),
    date: effectiveDate,
    year,
    month,
    checkIn: vacation ? null : toTimestamp(date, checkInTime),
    checkOut: vacation ? null : checkOutTime ? toTimestamp(date, checkOutTime) : null,
    vacation,
    vacation_type: vacation ? vacation_type : null,
    vacation_start: vacation ? vacation_start : null,
    vacation_end: vacation ? vacation_end : null,
    createdAt: serverTimestamp(),
    createdByAdmin: { uid: adminUser.uid, name: adminUser.name },
    lastEditedBy: null,
    lastEditedAt: null,
  }

  const docRef = await addDoc(getAttendanceCollection(), attendanceDoc)
  return { success: true, docId: docRef.id }
}

/**
 * Admin: edit attendance record (times or vacation fields)
 * @param {string} docId - Document ID
 * @param {Object} data - { date, checkInTime, checkOutTime, adminUser, vacation?, vacation_type?, vacation_start?, vacation_end? }
 * @returns {Promise<{ success: boolean }>}
 */
export async function adminEditAttendance(
  docId,
  {
    date,
    checkInTime,
    checkOutTime,
    adminUser,
    previousCheckIn,
    previousCheckOut,
    vacation,
    vacation_type,
    vacation_start,
    vacation_end,
    wasVacation,
    previousVacationType,
    previousVacationStart,
    previousVacationEnd,
  }
) {
  const db = getFirebaseDb()
  const docRef = doc(db, ...COLLECTION_PATH, docId)

  const editedAt = new Date().toISOString()

  // Determine what kind of edit was made
  let editType
  if (vacation === true) {
    editType = wasVacation ? 'vacation' : 'to_vacation'
  } else if (vacation === false) {
    editType = wasVacation ? 'from_vacation' : 'times'
  } else {
    editType = 'times'
  }

  const historyEntry = { adminUid: adminUser.uid, adminName: adminUser.name, editedAt, editType }

  if (editType === 'vacation') {
    historyEntry.previousVacationType = previousVacationType || null
    historyEntry.newVacationType = vacation_type || null
    historyEntry.previousVacationStart = previousVacationStart || null
    historyEntry.newVacationStart = vacation_start || null
    historyEntry.previousVacationEnd = previousVacationEnd || null
    historyEntry.newVacationEnd = vacation_end || null
  } else if (editType === 'to_vacation') {
    historyEntry.previousCheckIn = previousCheckIn || null
    historyEntry.previousCheckOut = previousCheckOut || null
    historyEntry.newVacationType = vacation_type || null
    historyEntry.newVacationStart = vacation_start || null
    historyEntry.newVacationEnd = vacation_end || null
  } else if (editType === 'from_vacation') {
    historyEntry.previousVacationType = previousVacationType || null
    historyEntry.previousVacationStart = previousVacationStart || null
    historyEntry.previousVacationEnd = previousVacationEnd || null
    historyEntry.newCheckIn = checkInTime || null
    historyEntry.newCheckOut = checkOutTime || null
  } else {
    historyEntry.previousCheckIn = previousCheckIn || null
    historyEntry.previousCheckOut = previousCheckOut || null
    historyEntry.newCheckIn = checkInTime || null
    historyEntry.newCheckOut = checkOutTime || null
  }

  const updates = {
    lastEditedBy: { uid: adminUser.uid, name: adminUser.name },
    lastEditedAt: editedAt,
    editHistory: arrayUnion(historyEntry),
  }

  // Handle vacation type conversion / editing
  if (vacation === true) {
    updates.vacation = true
    updates.vacation_type = vacation_type || null
    updates.vacation_start = vacation_start || null
    updates.vacation_end = vacation_end || null
    updates.checkIn = null
    updates.checkOut = null
    const effectiveDate = vacation_start || date
    if (effectiveDate) {
      updates.date = effectiveDate
      const { year, month } = parseDateParts(effectiveDate)
      updates.year = year
      updates.month = month
    }
  } else if (vacation === false) {
    updates.vacation = false
    updates.vacation_type = null
    updates.vacation_start = null
    updates.vacation_end = null
    if (date) {
      const { year, month } = parseDateParts(date)
      updates.year = year
      updates.month = month
    }
    if (checkInTime && date) {
      updates.checkIn = toTimestamp(date, checkInTime)
    }
    if (checkOutTime && date) {
      updates.checkOut = toTimestamp(date, checkOutTime)
    } else if (checkOutTime === '') {
      updates.checkOut = null
    }
  } else {
    // Legacy path: no vacation parameter passed, only edit times
    if (date) {
      const { year, month } = parseDateParts(date)
      updates.year = year
      updates.month = month
    }
    if (checkInTime && date) {
      updates.checkIn = toTimestamp(date, checkInTime)
    }
    if (checkOutTime && date) {
      updates.checkOut = toTimestamp(date, checkOutTime)
    } else if (checkOutTime === '') {
      updates.checkOut = null
    }
  }

  await updateDoc(docRef, updates)
  return { success: true }
}

/**
 * Admin: delete attendance record
 * @param {string} docId - Document ID
 * @returns {Promise<{ success: boolean }>}
 */
export async function deleteAttendance(docId) {
  const db = getFirebaseDb()
  const docRef = doc(db, ...COLLECTION_PATH, docId)
  await deleteDoc(docRef)
  return { success: true }
}

// Vacation records are stored on their start date (`date` = vacation_start), so a month
// query alone misses a vacation that started in a previous month and spills into this one.
// Month fetches look this many months back and keep only spill-in vacations from that
// window (3 months covers even long absences like miluim).
const VACATION_LOOKBACK_MONTHS = 3

/**
 * Build the query window and month start for a month fetch with vacation lookback
 * @param {number} year - Year (e.g., 2026)
 * @param {number} month - Month (1-12)
 * @returns {{ monthStart: string, queryStart: string, queryEnd: string }}
 */
function getMonthFetchWindow(year, month) {
  const monthStart = `${year}-${String(month).padStart(2, '0')}-01`
  const queryEnd = `${year}-${String(month).padStart(2, '0')}-31`
  const lookback = new Date(year, month - 1 - VACATION_LOOKBACK_MONTHS, 1)
  const queryStart = `${lookback.getFullYear()}-${String(lookback.getMonth() + 1).padStart(2, '0')}-01`
  return { monthStart, queryStart, queryEnd }
}

/**
 * Keep records dated inside the target month, plus earlier vacation records whose
 * range spills into it (the report layer clamps their day count to the month)
 * @param {Array} records - Records from the widened query window
 * @param {string} monthStart - 'YYYY-MM-01' of the target month
 * @returns {Array}
 */
function filterMonthRecords(records, monthStart) {
  return records.filter(
    (rec) =>
      rec.date >= monthStart || (rec.vacation && rec.vacation_end && rec.vacation_end >= monthStart)
  )
}

/**
 * Fetch attendance records for a specific agent and month (one-time query).
 * Includes vacation records from previous months whose range spills into the month.
 * @param {string} agentUid - Agent's Firebase UID
 * @param {number} year - Year (e.g., 2026)
 * @param {number} month - Month (1-12)
 * @returns {Promise<Array>} Array of { id, ...data } records sorted by date ascending
 */
export async function fetchMonthAttendance(agentUid, year, month) {
  const { monthStart, queryStart, queryEnd } = getMonthFetchWindow(year, month)

  const q = query(
    getAttendanceCollection(),
    where('agentUid', '==', agentUid),
    where('date', '>=', queryStart),
    where('date', '<=', queryEnd),
    orderBy('date', 'asc')
  )

  const snapshot = await getDocs(q)
  const records = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))
  return filterMonthRecords(records, monthStart)
}

/**
 * Fetch attendance records for ALL agents for a given month (one-time query).
 * Includes vacation records from previous months whose range spills into the month.
 * @param {number} year - Year (e.g., 2026)
 * @param {number} month - Month (1-12)
 * @returns {Promise<Array>} Array of { id, ...data } records sorted by date ascending
 */
export async function fetchAllAgentsMonthAttendance(year, month) {
  const { monthStart, queryStart, queryEnd } = getMonthFetchWindow(year, month)

  const q = query(
    getAttendanceCollection(),
    where('date', '>=', queryStart),
    where('date', '<=', queryEnd),
    orderBy('date', 'asc')
  )

  const snapshot = await getDocs(q)
  const records = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))
  return filterMonthRecords(records, monthStart)
}
